// Centralized rendering of pentest status: a colored pill (StatusChip) and a
// dropdown trigger styled to match (StatusSelect). Mirrors the look of the
// ProfileCombobox dropdown in the Tools tab so all PT-area selectors feel
// like one family.
import { useEffect, useRef, useState } from 'react';
import { Activity, Archive, Check, CheckCircle2, ChevronDown, PauseCircle } from 'lucide-react';

export const STATUS_META = {
  planning: {
    label: 'Planning',
    icon: PauseCircle,
    chip: 'bg-amber-100 text-amber-700 border-amber-300',
    dot: 'bg-amber-400',
    accent: 'amber',
    description: 'Scope being defined; no scans yet',
  },
  in_progress: {
    label: 'In Progress',
    icon: Activity,
    chip: 'bg-cyan-100 text-cyan-700 border-cyan-300',
    dot: 'bg-cyan-400 animate-pulse',
    accent: 'cyan',
    description: 'Active engagement; scans + findings in flight',
  },
  complete: {
    label: 'Complete',
    icon: CheckCircle2,
    chip: 'bg-wasabi-100 text-wasabi-700 border-wasabi-300',
    dot: 'bg-shinobi-green',
    accent: 'green',
    description: 'Engagement closed; report delivered',
  },
  archived: {
    label: 'Archived',
    icon: Archive,
    chip: 'bg-slate-100 text-slate-700 border-slate-300',
    dot: 'bg-slate-400',
    accent: 'slate',
    description: 'Hidden from default views; kept for history',
  },
};

const STATUS_ORDER = ['planning', 'in_progress', 'complete', 'archived'];

export function StatusChip({ status, size = 'md' }) {
  const meta = STATUS_META[status];
  if (!meta) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-cyber-300/50 bg-cyber-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">
        {status}
      </span>
    );
  }
  const Icon = meta.icon;
  const sz = size === 'sm'
    ? 'px-2 py-0.5 text-[10px] gap-1'
    : 'px-2.5 py-1 text-[11px] gap-1.5';
  return (
    <span
      className={`inline-flex items-center rounded-full border font-bold uppercase tracking-wider ${meta.chip} ${sz}`}
    >
      <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      {meta.label}
    </span>
  );
}

export function StatusSelect({ value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const meta = STATUS_META[value] || STATUS_META.planning;
  const SelectedIcon = meta.icon;

  return (
    <div ref={wrapRef} className="relative inline-block min-w-[14rem]">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`w-full text-left rounded-lg border bg-cyber-100 px-3 py-2.5 transition outline-none ${
          open
            ? 'border-shinobi-green/70 ring-1 ring-shinobi-green/30'
            : 'border-cyber-300/60 hover:border-cyber-300'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border ${meta.chip}`}>
            <SelectedIcon className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900">{meta.label}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{meta.description}</div>
          </div>
          {!disabled && (
            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
          )}
        </div>
      </button>

      {open && !disabled && (
        <div className="absolute z-30 mt-2 w-full rounded-xl border border-cyber-300/60 bg-cyber-50 shadow-[0_20px_60px_rgba(0,0,0,0.55)] overflow-hidden">
          {STATUS_ORDER.map((key) => {
            const m = STATUS_META[key];
            const Icon = m.icon;
            const selected = key === value;
            return (
              <button
                key={key}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onChange(key); setOpen(false); }}
                className={`w-full text-left px-3 py-2.5 flex items-center gap-3 border-l-2 transition ${
                  selected
                    ? 'bg-wasabi-100 border-shinobi-green'
                    : 'border-transparent hover:bg-cyber-200/40'
                }`}
              >
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border ${m.chip} shrink-0`}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${selected ? 'text-shinobi-green' : 'text-slate-900'}`}>
                    {m.label}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{m.description}</div>
                </div>
                {selected && <Check className="w-3.5 h-3.5 text-shinobi-green shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
